import { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";

// Sub-components
import { ShoppingHeader } from "../components/shopping/ShoppingHeader";
import { ShoppingFilters } from "../components/shopping/ShoppingFilters";
import { ShoppingList } from "../components/shopping/ShoppingList";
import { ShoppingItemDialog } from "../components/shopping/ShoppingItemDialog";
import { ClearCheckedButton } from "../components/shopping/ClearCheckedButton";

export default function ShoppingPage() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showChecked, setShowChecked] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    quantity: "",
    category: "General",
    assigned_to: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [itemsRes, membersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/shopping`),
        axios.get(`${API}/members`),
        axios.get(`${API}/shopping/categories`),
      ]);
      setItems(itemsRes.data);
      setMembers(membersRes.data);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      toast.error("Failed to fetch shopping list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build category colors map from fetched categories
  const categoryColors = categories.reduce((acc, cat) => {
    acc[cat.name] = cat.color;
    return acc;
  }, {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Item name is required");
      return;
    }

    try {
      const data = {
        ...formData,
        assigned_to: formData.assigned_to || null,
      };

      if (editingItem) {
        const res = await axios.put(`${API}/shopping/${editingItem.id}`, data);
        setItems((prev) =>
          prev.map((i) => (i.id === editingItem.id ? res.data : i))
        );
        toast.success("Item updated");
      } else {
        const res = await axios.post(`${API}/shopping`, data);
        setItems((prev) => [res.data, ...prev]);
        toast.success("Item added to list");
      }
      closeModal();
    } catch (error) {
      toast.error("Failed to save item");
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      quantity: item.quantity || "",
      category: item.category || "General",
      assigned_to: item.assigned_to || "",
    });
    setModalOpen(true);
  };

  const toggleChecked = async (item) => {
    try {
      const res = await axios.put(`${API}/shopping/${item.id}`, {
        checked: !item.checked,
      });
      setItems((prev) => prev.map((i) => (i.id === item.id ? res.data : i)));
      if (!item.checked) {
        toast.success(`${item.name} checked off!`);
      }
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/shopping/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Item removed");
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const clearChecked = async () => {
    try {
      const res = await axios.post(`${API}/shopping/clear-checked`);
      setItems((prev) => prev.filter((i) => !i.checked));
      toast.success(`Cleared ${res.data.deleted_count} items`);
    } catch (error) {
      toast.error("Failed to clear items");
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setFormData({
      name: "",
      quantity: "",
      category: categories.length > 0 ? categories[0].name : "General",
      assigned_to: "",
    });
  };

  const filteredItems = items.filter((item) => {
    if (categoryFilter !== "all" && item.category !== categoryFilter)
      return false;
    if (!showChecked && item.checked) return false;
    return true;
  });

  // Group items by category, then sort: unchecked first (alphabetically), then checked (alphabetically)
  const groupedItems = filteredItems.reduce((acc, item) => {
    const cat = item.category || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Sort items within each category
  Object.keys(groupedItems).forEach((cat) => {
    groupedItems[cat].sort((a, b) => {
      if (a.checked !== b.checked) {
        return a.checked ? 1 : -1;
      }
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  });

  const uncheckedCount = items.filter((i) => !i.checked).length;
  const checkedCount = items.filter((i) => i.checked).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shopping-page">
      <ShoppingHeader
        uncheckedCount={uncheckedCount}
        checkedCount={checkedCount}
        onAddItem={() => setModalOpen(true)}
      />

      <ShoppingFilters
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        showChecked={showChecked}
        setShowChecked={setShowChecked}
        categories={categories}
      />

      <ShoppingList
        groupedItems={groupedItems}
        categoryColors={categoryColors}
        onToggleItem={toggleChecked}
        onEditItem={openEditModal}
        onDeleteItem={deleteItem}
      />

      <ClearCheckedButton checkedCount={checkedCount} onClear={clearChecked} />

      <ShoppingItemDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        isEditing={!!editingItem}
        categories={categories}
        members={members}
      />
    </div>
  );
}
